import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchPagesRequest,
  fetchPageRequest,
  submitPageRequest,
  deletePageRequest,
  pageFormReset,
  createPagesFromTemplateRequest,
  createPageFromTemplateRequest,
  fetchDraftsRequest,
  createDraftRequest,
  updateDraftRequest,
  draftFormReset,
} from "@/store/slices/page.slice";
import { Page, CreatePageRequest, PageDraftResponse } from "@/types/models/page";
import { apiClient } from "@/lib/apiClient";

export const usePages = () => {
  const dispatch = useAppDispatch();
  // Guard against missing `state.page` (e.g. when a different Redux provider is active)
  const pageState = useAppSelector(
    (state) => (state && (state as any).page) || null,
  );
  const {
    items: pages = [],
    loading = false,
    pagesFetched = false,
    error = null,
    pagination = { page: 0, total: 0, pageSize: 10, totalPages: 0 },
    form = { loading: false, success: false, error: null },
    currentPage = null,
    currentPageLoading = false,
    currentPageError = null,
  } = pageState || {};

  const fetchPages = useCallback(
    (page: number = 1, pageSize: number = 10) => {
      dispatch(fetchPagesRequest({ page, pageSize }));
    },
    [dispatch],
  );

  const fetchPage = useCallback(
    (pageId: string) => {
      dispatch(fetchPageRequest({ pageId }));
    },
    [dispatch],
  );

  const createPage = useCallback(
    (pageData: Page) => {
      dispatch(submitPageRequest({ data: pageData }));
    },
    [dispatch],
  );

  const updatePage = useCallback(
    (pageId: string, pageData: Partial<Page>) => {
      dispatch(submitPageRequest({ data: pageData as Page, pageId }));
    },
    [dispatch],
  );

  const deletePage = useCallback(
    (pageId: string) => {
      dispatch(deletePageRequest({ pageId }));
    },
    [dispatch],
  );

  const resetForm = useCallback(() => {
    dispatch(pageFormReset());
  }, [dispatch]);

  const createPagesFromTemplate = useCallback(
    (templateId: string, pageIds?: string[]) => {
      dispatch(createPagesFromTemplateRequest({ templateId, pageIds }));
    },
    [dispatch],
  );

  const createPageFromTemplate = useCallback(
    (pageData: CreatePageRequest) => {
      dispatch(createPageFromTemplateRequest(pageData));
    },
    [dispatch],
  );

  const fetchDrafts = useCallback(
    (pageId: string) => {
      dispatch(fetchDraftsRequest({ pageId }));
    },
    [dispatch],
  );

  const createDraft = useCallback(
    (pageId: string) => {
      dispatch(createDraftRequest({ pageId }));
    },
    [dispatch],
  );

  const updateDraft = useCallback(
    (pageId: string, draftId: string) => {
      dispatch(updateDraftRequest({ pageId, draftId }));
    },
    [dispatch],
  );

  const getPageDraft = useCallback(async (pageId: string, draftId: string) => {
    return await apiClient.get<PageDraftResponse>(
      `/api/v1/admin/:storeId/web-builder/pages/${pageId}/drafts/${draftId}`,
    );
  }, []);

  const resetDraftForm = useCallback(() => {
    dispatch(draftFormReset());
  }, [dispatch]);

  const actions = useMemo(
    () => ({
      fetchPages,
      fetchPage,
      createPage,
      updatePage,
      deletePage,
      resetForm,
      createPagesFromTemplate,
      createPageFromTemplate,
      fetchDrafts,
      createDraft,
      updateDraft,
      getPageDraft,
      resetDraftForm,
    }),
    [
      fetchPages,
      fetchPage,
      createPage,
      updatePage,
      deletePage,
      resetForm,
      createPagesFromTemplate,
      createPageFromTemplate,
      fetchDrafts,
      createDraft,
      updateDraft,
      getPageDraft,
      resetDraftForm,
    ],
  );

  return {
    pages,
    loading,
    pagesFetched,
    error,
    pagination,
    form,
    currentPage,
    currentPageLoading,
    currentPageError,
    actions,
  };
};
